import type { UseQueryOptions } from "@tanstack/react-query";
import { queryOptions, skipToken, useQuery } from "@tanstack/react-query";
import api from "../api";
import urls from "../urls";
import type { SiteWizard } from "../utils/backend-types";
import { camelize } from "../utils/SnakeCamelConversion";

export function siteWizardQuery(
  siteId?: number,
  options?: Partial<UseQueryOptions<SiteWizard>>
) {
  return queryOptions({
    queryKey: ["siteWizard", { siteId }],
    queryFn: siteId ? () => fetchSiteWizard(siteId) : skipToken,
    ...options,
    enabled: !!siteId && (!options || options.enabled),
    retry: false
  });
}

export function useSiteWizard(
  siteId: number | undefined,
  options?: Partial<UseQueryOptions<SiteWizard>>
) {
  return useQuery(siteWizardQuery(siteId, options));
}

async function fetchSiteWizard(siteId: number) {
  const siteWizardUrl = urls.api.siteWizard(siteId);
  const response = await api.get(siteWizardUrl);

  if (!response.data) {
    return undefined;
  }

  const formattedData = response.data;

  if (formattedData.phases) {
    Object.keys(formattedData.phases).forEach((phase) => {
      formattedData.phases[phase]?.missingData?.forEach(
        (missingData, dataIndex) => {
          missingData.missingFields.forEach((missingField, fieldIndex) => {
            formattedData.phases[phase].missingData[dataIndex].missingFields[
              fieldIndex
            ] = camelize(missingField);
          });
        }
      );
      formattedData.phases[phase]?.exclusiveOrFields?.forEach(
        (group, groupIndex) => {
          group.forEach((field, index) => {
            formattedData.phases[phase].exclusiveOrFields[groupIndex][index] =
              camelize(field);
          });
        }
      );
    });
  }

  return formattedData;
}
